import { onMounted, onUnmounted, readonly, ref, watch } from 'vue'
import { useTokenStore } from '@/features/auth/store'
import { queryCache, type QueryKey } from '@/queries'
import { sseService } from '@/api/sseClient'
import { webSocketService } from '@/service/websocketService'
import type { SseConnectionState } from '@/types/sse'

/**
 * Transport switch for the WS → SSE migration. Defaults to SSE; set
 * VITE_NOTIFICATIONS_TRANSPORT=ws to roll back without a revert.
 */
const useSse = import.meta.env.VITE_NOTIFICATIONS_TRANSPORT !== 'ws'

const isNotificationsKey = (key: QueryKey) =>
  Array.isArray(key) ? key[0] === 'notifications' : key === 'notifications'

/**
 * The DB is the source of truth: rather than trusting the event to carry the
 * new item, any notification signal drops the cached lists so live views
 * refetch. This also covers events missed while the stream was down.
 */
function reconcileNotifications(): void {
  queryCache.invalidate((key) => isNotificationsKey(key))
}

export function useNotificationsStream() {
  const tokenStore = useTokenStore()
  const connectionState = ref<SseConnectionState>(sseService.connectionState)
  const isConnected = ref(sseService.isConnected)

  let unsubscribeFrames: (() => void) | undefined
  let unsubscribeState: (() => void) | undefined
  let wasConnected = false

  const handleState = (state: SseConnectionState) => {
    connectionState.value = state
    isConnected.value = state === 'connected'

    // Reconnect edge — events are at-most-once, so re-read from the backend.
    if (state === 'connected' && !wasConnected) {
      reconcileNotifications()
    }
    wasConnected = state === 'connected'
  }

  const handleFrame = (frame: { event: string; data: string }) => {
    // chain.completed is consumed by the search chain resolver, not here.
    if (frame.event === 'chain.completed') return

    let payloadType: string | undefined
    try {
      const parsed = JSON.parse(frame.data) as { type?: string }
      payloadType = parsed?.type ?? (frame.event === 'message' ? undefined : frame.event)
    } catch {
      // Non-JSON frames (heartbeats, plain pings) carry no dispatch information.
      return
    }

    // An untyped JSON frame (e.g. a keepalive) is not a notification signal.
    if (!payloadType || payloadType === 'chain.completed') return

    reconcileNotifications()
  }

  const start = () => {
    if (!tokenStore.accessToken) return

    if (useSse) {
      sseService.start(() => tokenStore.accessToken ?? null)
    } else {
      void webSocketService.connect(tokenStore.accessToken).catch((error) => {
        console.error('Failed to connect WebSocket:', error)
      })
    }
  }

  const stop = () => {
    if (useSse) {
      sseService.stop()
    } else {
      webSocketService.disconnect()
    }
    wasConnected = false
  }

  watch(
    () => tokenStore.accessToken,
    (token, previousToken) => {
      if (token && token !== previousToken) {
        stop()
        start()
      } else if (!token && previousToken) {
        stop()
      }
    }
  )

  onMounted(() => {
    if (useSse) {
      unsubscribeFrames = sseService.subscribe(handleFrame)
      unsubscribeState = sseService.subscribeState(handleState)
    }
    start()
  })

  onUnmounted(() => {
    unsubscribeFrames?.()
    unsubscribeState?.()
    stop()
  })

  return {
    isConnected: readonly(isConnected),
    connectionState: readonly(connectionState)
  }
}
